/**
 * VPA (Volume-Price Analysis) Scanner Dashboard
 * Absorption, test supply, breakout, upthrust detection with trading plans
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { vpaAPI } from '../services/api';
import { colors } from '../theme';
import {
  HeaderSection,
  LeaderboardSection,
  RecommendationsSection,
  ScanHistorySection,
  SignalHistorySection,
  SummaryTilesSection,
  TickerDrawer,
} from '../components/vpa';


// ══════════════════════════════════════════════════════════
// MAIN: VPA PAGE
// ══════════════════════════════════════════════════════════
export default function VPA() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';

  const [loading, setLoading] = useState(false);
  const [exportingReportImage, setExportingReportImage] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [mode, setMode] = useState('default'); // default | conservative
  const [actionFilter, setActionFilter] = useState('READY');
  const [sortField, setSortField] = useState('vpa_score');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTicker, setSearchTicker] = useState('');
  const [lookupTicker, setLookupTicker] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [scanDates, setScanDates] = useState([]); // [{date,total,ready_count,...}]
  const [selectedDate, setSelectedDate] = useState(null); // null = latest (live scan)
  const [signalHistory, setSignalHistory] = useState(null); // {win_rate, avg_pnl, signals:[]}
  const recommendationsExportRef = useRef(null);

  // Ticker dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTicker, setDialogTicker] = useState(null);

  const showSnack = useCallback((msg, severity = 'success') => {
    setSnack({ open: true, msg, severity });
  }, []);
  const openTickerDrawer = useCallback((tickerOrRow) => {
    setDialogTicker(tickerOrRow);
    setDialogOpen(true);
  }, []);

  const handleExportReportImage = useCallback(async () => {
    if (!recommendationsExportRef.current || exportingReportImage || !(scanResult?.results || []).length) {
      return;
    }

    try {
      setExportingReportImage(true);

      const canvas = await html2canvas(recommendationsExportRef.current, {
        backgroundColor: colors.gray[50],
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: window.innerWidth,
        windowHeight: Math.max(recommendationsExportRef.current.scrollHeight, window.innerHeight),
      });

      const link = document.createElement('a');
      const asOfDate = (scanResult?.scan_date || selectedDate || new Date().toISOString().slice(0, 10)).toString().slice(0, 10);
      link.download = `vpa-recommendations-${mode}-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to export VPA report image', error);
      setSnack({
        open: true,
        msg: t('common.exportFailed', 'Không thể xuất ảnh'),
        severity: 'error',
      });
    } finally {
      setExportingReportImage(false);
    }
  }, [exportingReportImage, mode, scanResult, selectedDate, t]);

  const handleLookupTicker = useCallback(async () => {
    const normalizedTicker = lookupTicker.trim().toUpperCase();
    if (!normalizedTicker) {
      showSnack(t('vpa.lookup.empty'), 'warning');
      return;
    }

    setLookupLoading(true);
    try {
      const response = await vpaAPI.getTicker(normalizedTicker);
      const history = response.data?.history || [];

      if (!Array.isArray(history) || history.length === 0) {
        showSnack(t('vpa.lookup.noData', { ticker: normalizedTicker }), 'warning');
        return;
      }

      const existingRow = (scanResult?.results || []).find((row) => row.ticker === normalizedTicker);
      openTickerDrawer(existingRow || normalizedTicker);
      setLookupTicker(normalizedTicker);
    } catch (error) {
      showSnack(t('vpa.lookup.failed', { ticker: normalizedTicker }), 'error');
    } finally {
      setLookupLoading(false);
    }
  }, [lookupTicker, openTickerDrawer, scanResult, showSnack, t]);

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (selectedDate) {
        // Historical date: read persisted leaderboard + summary
        const [lbRes, sumRes] = await Promise.all([
          vpaAPI.getLeaderboard({ scan_date: selectedDate, top_n: 200 }),
          vpaAPI.getSummary({ scan_date: selectedDate }),
        ]);
        setScanResult({
          scan_date: selectedDate,
          total_scanned: sumRes.data?.summary?.total || 0,
          total_results: (lbRes.data?.results || lbRes.data || []).length,
          results: lbRes.data?.results || lbRes.data || [],
          summary: sumRes.data?.summary || {},
        });
      } else {
        const res = await vpaAPI.scan({
          top_n: 100,
          persist: true,
          conservative: mode === 'conservative',
        });
        setScanResult(res.data);
      }
    } catch (err) {
      showSnack(t('vpa.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  }, [mode, selectedDate, showSnack, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load scan-date timeline once
  useEffect(() => {
    vpaAPI.getDates(60)
      .then(res => setScanDates(res.data?.history || []))
      .catch(() => setScanDates([]));
  }, []);

  // Load signal history with outcome tracking (READY + WATCH_CLOSE, last 30 days)
  useEffect(() => {
    vpaAPI.getSignalHistory(30)
      .then(res => setSignalHistory(res.data || null))
      .catch(() => setSignalHistory(null));
  }, []);

  // ── Derived data ──
  const results = scanResult?.results || [];
  const summary = scanResult?.summary || {};

  const filteredResults = results
    .filter((r) => {
      if (actionFilter === 'all') return true;
      return r.action_label === actionFilter;
    })
    .filter((r) => {
      if (!searchTicker) return true;
      return (r.ticker || '').toLowerCase().includes(searchTicker.toLowerCase());
    })
    .sort((a, b) => {
      const getVal = (row) => {
        if (sortField === 'close') return row.close ?? 0;
        if (sortField === 'vpa_score') return row.vpa_score ?? 0;
        if (sortField === 'setup_score') return row.setup_score ?? 0;
        if (sortField === 'trend_score') return row.trend_score ?? 0;
        if (sortField === 'penalty_score') return row.penalty_score ?? 0;
        if (sortField === 'trap_risk') return row.trap_risk ?? 0;
        if (sortField === 'breakout_quality') return row.breakout_quality ?? 0;
        if (sortField === 'vol_ratio') return row.vol_ratio ?? 0;
        if (sortField === 'rr_target_2') return row.rr_target_2 ?? 0;
        if (sortField === 'avg_value_20d') return row.avg_value_20d ?? 0;
        if (sortField === 'confluence') return row.confluence ?? 0;
        return 0;
      };
      const diff = getVal(b) - getVal(a);
      return sortDir === 'desc' ? diff : -diff;
    });

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir((d) => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }, [sortField]);

  // ── Summary values ──
  const totalScanned = scanResult?.total_scanned ?? summary?.total ?? 0;
  const readyCount = summary?.ready_count ?? 0;
  const watchCount = summary?.watch_count ?? 0;
  const radarCount = summary?.radar_count ?? 0;
  const avoidCount = summary?.avoid_count ?? 0;
  const avgScore = summary?.avg_score ?? 0;
  const recommendations = scanResult?.recommendations || { items: [], counts: { ready: 0, watch: 0 } };
  const recItems = recommendations.items || [];
  const hasScanResult = Boolean(scanResult);
  const hasResults = (scanResult?.results || []).length > 0;

  return (
    <Box sx={{ py: { xs: 1.5, sm: 2.5 }, px: 0 }}>
      <HeaderSection
        t={t}
        scanDate={scanResult?.scan_date}
        mode={mode}
        setMode={setMode}
        loading={loading}
        fetchData={fetchData}
        lookupTicker={lookupTicker}
        setLookupTicker={setLookupTicker}
        handleLookupTicker={handleLookupTicker}
        lookupLoading={lookupLoading}
      />

      <SummaryTilesSection
        t={t}
        loading={loading}
        hasScanResult={hasScanResult}
        totalScanned={totalScanned}
        filteredCount={filteredResults.length}
        readyCount={readyCount}
        watchCount={watchCount}
        radarCount={radarCount}
        avoidCount={avoidCount}
        avgScore={avgScore}
        maxScore={summary?.max_score}
      />

      <RecommendationsSection
        t={t}
        lang={lang}
        loading={loading}
        recItems={recItems}
        recommendations={recommendations}
        exportRef={recommendationsExportRef}
        exportingReportImage={exportingReportImage}
        handleExportReportImage={handleExportReportImage}
        hasResults={hasResults}
        openTickerDrawer={openTickerDrawer}
      />

      <LeaderboardSection
        t={t}
        loading={loading}
        hasScanResult={hasScanResult}
        results={results}
        filteredResults={filteredResults}
        actionFilter={actionFilter}
        setActionFilter={setActionFilter}
        searchTicker={searchTicker}
        setSearchTicker={setSearchTicker}
        handleSort={handleSort}
        sortField={sortField}
        sortDir={sortDir}
        openTickerDrawer={openTickerDrawer}
      />

      <ScanHistorySection
        t={t}
        scanDates={scanDates}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      <SignalHistorySection
        t={t}
        signalHistory={signalHistory}
        openTickerDrawer={openTickerDrawer}
      />

      {/* ── Ticker Detail Drawer ── */}
      <TickerDrawer
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        ticker={dialogTicker?.ticker || dialogTicker}
        initialRow={typeof dialogTicker === 'object' ? dialogTicker : null}
      />

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
